import React, { useEffect, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import ImageExtension from "@tiptap/extension-image";
import Color from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import Placeholder from "@tiptap/extension-placeholder";
import BlogEditorToolbar from "./BlogEditorToolbar";
import BlogEditorImageDrawer from "./BlogEditorImageDrawer";
import KeyInsightsEditor from "./KeyInsightsEditor";
import { FontSize } from "./extensions/fontSize";
import { MediaSelectorModal } from "../../../../shared/components/modals";
import { BlogService } from "../../../../services/blogService";
import { BlogCategoryDto } from "../../../../types/app/blogTypes";
import { BLOG_SITE_URL } from "../../../../services/paths";
import { LoadingItem } from "../../../../shared/components/loading";

interface BlogPost {
  id?: string;
  slug?: string;
  title?: string;
  excerpt?: string;
  content?: string;
  featuredImage?: string;
  category?: string;
  categories?: string[];
  tags?: string[];
  author?: {
    name: string;
    avatar: string;
  };
  publishDate?: string;
  readTime?: string;
  metaTitle?: string;
  metaDescription?: string;
  views?: number;
  featured?: boolean;
  keyInsights?: string[];
  status?: "draft" | "published" | "trashed";
}

interface PostEditorProps {
  post: Partial<BlogPost>;
  onSave: (post: Partial<BlogPost>, isDraft: boolean) => void;
  onCancel: () => void;
  onChange?: () => void;
  isNew: boolean;
  isSaving?: boolean;
}

const getReadTimeValue = (readTime?: string) =>
  readTime ? readTime.replace(" min read", "") : "";

const getPlainText = (html: string) =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

const getInitialCategories = (post: Partial<BlogPost>) => {
  const categoryId = (post as any).categoryId;

  if (categoryId) {
    return [categoryId];
  }

  if (post.categories?.length) {
    return post.categories;
  }

  return post.category ? [post.category] : [];
};

const getCleanKeyInsights = (keyInsights: string[]) =>
  keyInsights.filter((insight) => getPlainText(insight).length > 0);

const PostEditor: React.FC<PostEditorProps> = ({
  post,
  onSave,
  onCancel,
  onChange,
  isNew,
  isSaving = false,
}) => {
  const [title, setTitle] = useState(post.title || "");
  const [excerpt, setExcerpt] = useState(post.excerpt || "");
  const [readTime, setReadTime] = useState(getReadTimeValue(post.readTime));
  const [content, setContent] = useState(post.content || "");
  const [category, setCategory] = useState(getInitialCategories(post)[0] || "");
  const [categories, setCategories] = useState<string[]>(
    getInitialCategories(post),
  );
  const [tags, setTags] = useState(
    post.tags?.map((tag, index) => ({ id: index.toString(), text: tag })) || [],
  );
  const [keyInsights, setKeyInsights] = useState<string[]>(
    post.keyInsights || [],
  );
  const [featuredImage, setFeaturedImage] = useState(post.featuredImage || "");
  const [showImageModal, setShowImageModal] = useState(false);
  const [showInlineImageModal, setShowInlineImageModal] = useState(false);
  const [availableCategories, setAvailableCategories] = useState<
    BlogCategoryDto[]
  >([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [savingMode, setSavingMode] = useState<"draft" | "publish" | null>(
    null,
  );
  const [, setToolbarVersion] = useState(0);
  const scrollIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isEditorScrolling, setIsEditorScrolling] = useState(false);
  const [tableMenu, setTableMenu] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      FontSize,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({ openOnClick: false }),
      ImageExtension,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder: "Write your post..." }),
    ],
    content: post.content || "",
    onSelectionUpdate: () => setToolbarVersion((version) => version + 1),
    onTransaction: () => setToolbarVersion((version) => version + 1),
    onUpdate: () => {
      setValidationErrors((prevErrors) => {
        if (!prevErrors.content) {
          return prevErrors;
        }

        const nextErrors = { ...prevErrors };
        delete nextErrors.content;
        return nextErrors;
      });
      onChange?.();
    },
  });

  useEffect(() => {
    let isMounted = true;

    const fetchCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const data = await BlogService.getBlogCategories(false);
        if (isMounted) {
          setAvailableCategories(Array.isArray(data) ? data : []);
        }
      } catch (error) {
      } finally {
        if (isMounted) {
          setIsLoadingCategories(false);
        }
      }
    };

    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (post && !isNew) {
      const nextCategories = getInitialCategories(post);

      setTitle(post.title || "");
      setExcerpt(post.excerpt || "");
      setReadTime(getReadTimeValue(post.readTime));
      setContent(post.content || "");
      setCategory(nextCategories[0] || "");
      setCategories(nextCategories);
      setFeaturedImage(post.featuredImage || "");
      setTags(
        post.tags?.map((tag, index) => ({ id: index.toString(), text: tag })) ||
          [],
      );
      setKeyInsights(post.keyInsights || []);
    }
  }, [post, isNew]);

  useEffect(() => {
    if (editor && post && !isNew && post.content !== undefined) {
      editor.commands.setContent(post.content || "");
    }
  }, [post, isNew, editor]);

  useEffect(() => {
    if (!isSaving) {
      setSavingMode(null);
    }
  }, [isSaving]);

  useEffect(() => {
    if (!tableMenu) return;

    const closeMenu = () => setTableMenu(null);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };

    document.addEventListener("mousedown", closeMenu);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", closeMenu);
    window.addEventListener("scroll", closeMenu, true);

    return () => {
      document.removeEventListener("mousedown", closeMenu);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", closeMenu);
      window.removeEventListener("scroll", closeMenu, true);
    };
  }, [tableMenu]);

  useEffect(
    () => () => {
      if (scrollIdleTimerRef.current) {
        clearTimeout(scrollIdleTimerRef.current);
      }
    },
    [],
  );

  const handleEditorScroll = () => {
    setTableMenu(null);
    setIsEditorScrolling(true);

    if (scrollIdleTimerRef.current) {
      clearTimeout(scrollIdleTimerRef.current);
    }

    scrollIdleTimerRef.current = setTimeout(() => {
      setIsEditorScrolling(false);
    }, 900);
  };

  const handleTableContextMenu = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    if (!editor) return;

    const target = event.target as HTMLElement;
    const cell = target.closest("td, th");
    if (!cell || !event.currentTarget.contains(cell)) return;

    event.preventDefault();

    try {
      const cellPosition = editor.view.posAtDOM(cell, 0);
      const selectionPosition = Math.min(
        cellPosition + 1,
        editor.state.doc.content.size,
      );
      editor.chain().focus().setTextSelection(selectionPosition).run();
    } catch {
      editor.commands.focus();
    }

    const menuWidth = 176;
    const menuHeight = 92;
    setTableMenu({
      x: Math.min(event.clientX, window.innerWidth - menuWidth - 8),
      y: Math.min(event.clientY, window.innerHeight - menuHeight - 8),
    });
  };

  const deleteTableRow = () => {
    editor?.chain().focus().deleteRow().run();
    setTableMenu(null);
  };

  const deleteTableColumn = () => {
    editor?.chain().focus().deleteColumn().run();
    setTableMenu(null);
  };

  const clearFieldError = (fieldName: string) => {
    if (validationErrors[fieldName]) {
      setValidationErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors[fieldName];
        return nextErrors;
      });
    }
  };

  const validateForm = (contentValue: string, keyInsightValues: string[]) => {
    const errors: Record<string, string> = {};

    if (!title.trim()) {
      errors.title = "Title is required";
    }

    if (!getPlainText(contentValue)) {
      errors.content = "Content is required";
    }

    if (!categories.length) {
      errors.categories = "Error: Select a blog category";
    }

    if (!featuredImage.trim()) {
      errors.featuredImage = "Error: Select a featured image";
    }

    if (!keyInsightValues.length) {
      errors.keyInsights = "Error: Add at least one key insight";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value);
    clearFieldError("title");
    onChange?.();
  };

  const handleExcerptChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setExcerpt(event.target.value);
    onChange?.();
  };

  const handleReadTimeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setReadTime(event.target.value);
    onChange?.();
  };

  const handleInlineImageConfirm = (imageUrl: string) => {
    if (imageUrl) {
      editor?.chain().focus().setImage({ src: imageUrl }).run();
    }

    setShowInlineImageModal(false);
  };

  const handleInsertTable = (bodyRows: number, columns: number) => {
    editor
      ?.chain()
      .focus()
      .insertTable({ rows: bodyRows + 1, cols: columns, withHeaderRow: true })
      .run();
  };

  const handleCategoryChange = (categoryValue: string, isChecked: boolean) => {
    setCategories((prevCategories) => {
      const nextCategories = isChecked
        ? [...prevCategories, categoryValue]
        : prevCategories.filter((id) => id !== categoryValue);

      setCategory(nextCategories[0] || "");

      return nextCategories;
    });

    clearFieldError("categories");
    onChange?.();
  };

  const handleImageConfirm = (imageUrl: string) => {
    setFeaturedImage(imageUrl);
    clearFieldError("featuredImage");
    onChange?.();
  };

  const handleKeyInsightsChange = (nextKeyInsights: string[]) => {
    setKeyInsights(nextKeyInsights);
    clearFieldError("keyInsights");
    onChange?.();
  };

  const handleSave = (isDraft: boolean) => {
    const liveContent = editor?.getHTML() ?? content;
    const cleanKeyInsights = getCleanKeyInsights(keyInsights);

    if (!validateForm(liveContent, cleanKeyInsights)) {
      return;
    }

    setSavingMode(isDraft ? "draft" : "publish");
    onSave(
      {
        ...post,
        title,
        excerpt,
        content: liveContent,
        readTime: readTime ? `${readTime} min read` : "0 min read",
        category,
        categories,
        tags: tags.map((tag) => tag.text),
        keyInsights: cleanKeyInsights,
        featuredImage,
        status: isDraft ? "draft" : "published",
      },
      isDraft,
    );
  };

  return (
    <div className="post-editor">
      <div className="editor-header">
        <button className="btn-back" onClick={onCancel} type="button">
          Back to Posts
        </button>
      </div>

      <div className="editor-container">
        <div className="editor-main">
          <div className="title-section">
            <div className="field-group">
              <input
                type="text"
                className={`post-title-input ${
                  validationErrors.title ? "error" : ""
                }`}
                placeholder="Add title"
                value={title}
                onChange={handleTitleChange}
              />
              {validationErrors.title && (
                <span className="field-error">{validationErrors.title}</span>
              )}
            </div>

            <div className="permalink-section">
              <span className="permalink-base">{BLOG_SITE_URL}/</span>
              <span className="permalink-url-display">
                {post.slug || "Generated automatically after publishing"}
              </span>
            </div>

            <div className="field-group">
              <textarea
                className="post-excerpt-input"
                placeholder="Add a brief excerpt or summary (optional)"
                value={excerpt}
                onChange={handleExcerptChange}
                rows={1}
              />
            </div>

            <div className="field-group">
              <label className="field-label readtime-label">
                Read Time (minutes)
              </label>
              <input
                type="number"
                className="post-readtime-input"
                placeholder="5"
                value={readTime}
                onChange={handleReadTimeChange}
                min="0"
              />
            </div>
          </div>

          {validationErrors.content && (
            <span className="field-error">{validationErrors.content}</span>
          )}
          <div
            className={`editor-wrapper ${
              validationErrors.content ? "error" : ""
            }`}
          >
            <BlogEditorToolbar
              editor={editor}
              onInsertImage={() => setShowInlineImageModal(true)}
              onInsertTable={handleInsertTable}
            />
            <div
              className={`blog-editor-scroll-area ${
                isEditorScrolling ? "is-scrolling" : ""
              }`}
              onContextMenu={handleTableContextMenu}
              onScroll={handleEditorScroll}
            >
              <EditorContent
                editor={editor}
                className="custom-quill blog-quill-editor"
              />
            </div>
          </div>

          {tableMenu && (
            <div
              aria-label="Table actions"
              className="blog-table-context-menu"
              onMouseDown={(event) => event.stopPropagation()}
              role="menu"
              style={{ left: tableMenu.x, top: tableMenu.y }}
            >
              <button
                disabled={!editor?.can().deleteRow()}
                onClick={deleteTableRow}
                onMouseDown={(event) => event.preventDefault()}
                role="menuitem"
                type="button"
              >
                Delete row
              </button>
              <button
                disabled={!editor?.can().deleteColumn()}
                onClick={deleteTableColumn}
                onMouseDown={(event) => event.preventDefault()}
                role="menuitem"
                type="button"
              >
                Delete column
              </button>
            </div>
          )}

          <KeyInsightsEditor
            value={keyInsights}
            onChange={handleKeyInsightsChange}
            error={validationErrors.keyInsights}
          />
        </div>

        <div className="editor-sidebar">
          <div className="sidebar-section publish-section">
            <div className="publish-actions">
              <button
                className="btn-save-draft"
                disabled={isSaving}
                onClick={() => handleSave(true)}
                aria-busy={isSaving && savingMode === "draft"}
                type="button"
              >
                {isSaving && savingMode === "draft"
                  ? "Saving..."
                  : "Save Draft"}
              </button>
              <button
                className="btn-publish"
                disabled={isSaving}
                onClick={() => handleSave(false)}
                aria-busy={isSaving && savingMode === "publish"}
                type="button"
              >
                {isSaving && savingMode === "publish"
                  ? isNew
                    ? "Publishing..."
                    : "Updating..."
                  : isNew
                    ? "Publish"
                    : "Update"}
              </button>
            </div>
            {!isNew && (
              <div className="trash-link-container">
                <button className="btn-trash-link" type="button">
                  Move to Trash
                </button>
              </div>
            )}
          </div>

          <div
            className={`sidebar-section ${
              validationErrors.featuredImage ? "error" : ""
            }`}
          >
            <h3>Featured image *</h3>
            {validationErrors.featuredImage && (
              <span className="field-error">
                {validationErrors.featuredImage}
              </span>
            )}
            {featuredImage ? (
              <div className="featured-image-preview">
                <img src={featuredImage} alt="Featured" />
                <button
                  className="btn-remove-image"
                  onClick={() => {
                    setFeaturedImage("");
                    onChange?.();
                  }}
                  type="button"
                >
                  Remove
                </button>
              </div>
            ) : (
              <button
                className={`btn-set-image ${
                  validationErrors.featuredImage ? "error" : ""
                }`}
                onClick={() => setShowImageModal(true)}
                type="button"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.0013 6.66667C6.73768 6.66667 7.33464 6.06971 7.33464 5.33333C7.33464 4.59695 6.73768 4 6.0013 4C5.26492 4 4.66797 4.59695 4.66797 5.33333C4.66797 6.06971 5.26492 6.66667 6.0013 6.66667Z"
                    stroke="#292D32"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M8.66536 1.33203H5.9987C2.66536 1.33203 1.33203 2.66536 1.33203 5.9987V9.9987C1.33203 13.332 2.66536 14.6654 5.9987 14.6654H9.9987C13.332 14.6654 14.6654 13.332 14.6654 9.9987V6.66536"
                    stroke="#292D32"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M10.5 3.33203H14.1667"
                    stroke="#292D32"
                    strokeLinecap="round"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M12.332 5.16667V1.5"
                    stroke="#292D32"
                    strokeLinecap="round"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M1.78125 12.635L5.06792 10.4283C5.59458 10.075 6.35458 10.115 6.82792 10.5217L7.04792 10.715C7.56792 11.1617 8.40792 11.1617 8.92792 10.715L11.7012 8.335C12.2212 7.88833 13.0612 7.88833 13.5812 8.335L14.6679 9.26833"
                    stroke="#292D32"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                  />
                </svg>
                Set featured image
              </button>
            )}
          </div>

          <div
            className={`sidebar-section ${
              validationErrors.categories ? "error" : ""
            }`}
          >
            <h3>Categories *</h3>
            {validationErrors.categories && (
              <span className="field-error">{validationErrors.categories}</span>
            )}
            <div
              className={`categories-list ${
                validationErrors.categories ? "error" : ""
              }`}
            >
              {isLoadingCategories ? (
                <div className="sidebar-empty-state">
                  <LoadingItem />
                </div>
              ) : availableCategories.length === 0 ? (
                <div className="sidebar-empty-state">
                  No categories available.{" "}
                  <a
                    href="/app/blog/categories"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Create one
                  </a>
                </div>
              ) : (
                availableCategories.map((cat) => (
                  <label key={cat.id} className="category-checkbox">
                    <input
                      type="checkbox"
                      value={cat.id}
                      checked={categories.includes(cat.id)}
                      onChange={(event) =>
                        handleCategoryChange(cat.id, event.target.checked)
                      }
                    />
                    {cat.name}
                  </label>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <MediaSelectorModal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        onSelect={handleImageConfirm}
        title="Select Featured Image"
        currentImage={featuredImage}
      />

      <BlogEditorImageDrawer
        show={showInlineImageModal}
        onClose={() => setShowInlineImageModal(false)}
        onSelect={handleInlineImageConfirm}
        currentImageUrl={null}
        currentName={title || "Blog post image"}
      />
    </div>
  );
};

export default PostEditor;
